import React, { useState } from 'react';
import { Crosshair } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { MoneyInput } from '@components/MoneyInput';
import firearmService from '@services/firearmService';
import {
  FIREARM_KINDS,
  FIREARM_KIND_LABELS,
  FIREARM_STATUSES,
  FIREARM_STATUS_LABELS,
  type Firearm,
  type FirearmInput,
  type FirearmKind,
  type FirearmStatus,
} from '@/types/cac';

interface Props {
  firearm?: Firearm | null;
  onClose: () => void;
  onSaved: (firearm: Firearm) => void;
  onError: (err: unknown) => void;
}

interface FormState {
  kind: FirearmKind;
  brand: string;
  model: string;
  caliber: string;
  serial_number: string;
  sigma_number: string;
  acquisition_date: string;
  acquisition_value_cents: number | null;
  status: FirearmStatus;
  notes: string;
}

const labelCls = 'text-label block mb-1';

/** Cadastro/edição de arma (POST/PUT /firearms). Valor de aquisição em R$ com máscara → centavos. */
export const FirearmFormModal: React.FC<Props> = ({ firearm, onClose, onSaved, onError }) => {
  const isEdit = !!firearm;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(() => ({
    kind: firearm?.kind ?? 'pistola',
    brand: firearm?.brand ?? '',
    model: firearm?.model ?? '',
    caliber: firearm?.caliber ?? '',
    serial_number: firearm?.serial_number ?? '',
    sigma_number: firearm?.sigma_number ?? '',
    acquisition_date: firearm?.acquisition_date ?? '',
    acquisition_value_cents: firearm?.acquisition_value_cents ?? null,
    status: firearm?.status ?? 'active',
    notes: firearm?.notes ?? '',
  }));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const canSubmit = !saving && !!form.brand.trim() && !!form.model.trim() && !!form.caliber.trim() && !!form.serial_number.trim();

  const save = async () => {
    if (!canSubmit) return;
    const payload: FirearmInput = {
      kind: form.kind,
      brand: form.brand.trim(),
      model: form.model.trim(),
      caliber: form.caliber.trim(),
      serial_number: form.serial_number.trim(),
      sigma_number: form.sigma_number.trim() || null,
      acquisition_date: form.acquisition_date || null,
      acquisition_value_cents: form.acquisition_value_cents,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      onSaved(firearm ? await firearmService.update(firearm.id, payload) : await firearmService.create(payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Crosshair size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar arma' : 'Nova arma'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>
            Tipo <span className="text-red-500">*</span>
          </label>
          <select className="select-field" value={form.kind} onChange={(e) => set('kind', e.target.value as FirearmKind)}>
            {FIREARM_KINDS.map((k) => (
              <option key={k} value={k}>
                {FIREARM_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>
            Situação <span className="text-red-500">*</span>
          </label>
          <select className="select-field" value={form.status} onChange={(e) => set('status', e.target.value as FirearmStatus)}>
            {FIREARM_STATUSES.map((s) => (
              <option key={s} value={s}>
                {FIREARM_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>
            Marca <span className="text-red-500">*</span>
          </label>
          <input className="input-field" maxLength={100} value={form.brand} onChange={(e) => set('brand', e.target.value)} placeholder="Ex.: Taurus" />
        </div>
        <div>
          <label className={labelCls}>
            Modelo <span className="text-red-500">*</span>
          </label>
          <input className="input-field" maxLength={100} value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="Ex.: G3c" />
        </div>
        <div>
          <label className={labelCls}>
            Calibre <span className="text-red-500">*</span>
          </label>
          <input className="input-field" maxLength={50} value={form.caliber} onChange={(e) => set('caliber', e.target.value)} placeholder="Ex.: 9mm" />
        </div>
        <div>
          <label className={labelCls}>
            Nº de série <span className="text-red-500">*</span>
          </label>
          <input className="input-field" maxLength={100} value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Nº SIGMA</label>
          <input className="input-field" maxLength={100} value={form.sigma_number} onChange={(e) => set('sigma_number', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Data de aquisição</label>
          <DateInputBR className="input-field w-full" value={form.acquisition_date} onChange={(v) => set('acquisition_date', v)} />
        </div>
        <div>
          <label className={labelCls}>Valor de aquisição</label>
          <MoneyInput value={form.acquisition_value_cents} onChange={(c) => set('acquisition_value_cents', c)} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Observações</label>
          <textarea className="textarea-field" rows={3} maxLength={2000} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Cadastrar'}
        </Button>
      </div>
    </Modal>
  );
};
